import { useEffect } from 'react';

// Extend the Window interface directly within this file
declare global {
  interface Window {
    CanopyConnect: any;
  }
}

interface CanopyConnectProps {
  publicAlias?: string;  
  consentToken?: string;
  apiRequestImportKey?: string;
  onClose?: (doImport: boolean) => void;
}

function CanopyConnect({ publicAlias, consentToken, apiRequestImportKey, onClose }: CanopyConnectProps) {
  useEffect(() => {
    if (!publicAlias) return;

    // Check if the script is already loaded
    const existingScript = document.querySelector('#canopy-connect-script');
    const metaData = { LookupKey: apiRequestImportKey };

    const initializeHandler = () => {
      if (!window.CanopyConnect) {
        return;
      }

      const handler = window.CanopyConnect.create({
        publicAlias,
        consentToken,
        pullMetaData: metaData,
      });

      handler.open();


      handler.on("exit", () => {
        handler.destroy();

        if (onClose) {
          onClose(false); // Notify StartQuotePage that the modal was closed
        }
      });

      return () => {
        handler.off("exit");
      };
    };

    if (existingScript) {
      // If script is already in the document, proceed with initialization
      initializeHandler();
    } else {
      // Otherwise, create and load the script
      const script = document.createElement('script');
      script.src = 'https://cdn.usecanopy.com/v2/canopy-connect.js';
      script.id = 'canopy-connect-script';
      script.async = true;
      script.onload = initializeHandler;
      document.head.appendChild(script);
    }
  }, [publicAlias, consentToken, apiRequestImportKey, onClose]); 

  useEffect(() => {
    const messageListener = (event: MessageEvent) => {
      if (event.data && event.data.canopy_event === "status_update" && event.data.status === "SUCCESS") {
        if (onClose !== undefined) {
          onClose(true);
        }
      }
    };
    window.addEventListener("message", messageListener);

    return () => {
      window.removeEventListener("message", messageListener);
    };
  }, [onClose]);

  return null
}

export default CanopyConnect;
